<template>
  <div
    class="toc-navigation"
    :class="{expanded, 'is-resizing': isResizing, 'is-widget': isWidget}"
    :style="tocStyle"
  >
    <div>
      <div
        class="collapse"
      >
        <transition name="toc-content">
          <div v-show="expanded" class="toc-panel" @click.stop @mousedown.stop>
            <div class="toc-header">
              <search-type-ahead
                                 :placeholder="$t('searchInContent', { contentName: searchContentNameLabel })"
                                 :is-bom="false"
                                 :value="localQuery"
                                 :content="book"
                                 :show-filtered-count="shouldShowFilteredCount"
                                 :total-count="tocTotalCount"
                                 :total-displayed="tocTotalDisplayed"
                                 :clear-on-enter="false"
                                 class="toc-search"
                                 @enter="search"
                                 @clearClick="clearClick"
              >
                <div class="toc-try-this-attached">
                  <try-this-pill
                    :label="$t('trySearchingToc')"
                    :local-storage-property="'trySearchingToc'"
                    :tool-tip-width="'15em'"
                    :tool-tip-position="'is-bottom-left'"
                  />
                </div>
                <p v-if="hasTocQuery && canNavigateResults"
                   class="control">
                  <button class="button is-primary is-outlined previous-button"
                          :aria-label="$t('threeD.previousResult')"
                          @click="previousResult">
            <span class="icon is-medium">
              <i class="fa fa-chevron-left" />
            </span>
                  </button>
                </p>
                <p v-if="hasTocQuery && canNavigateResults"
                   class="control">
                  <button class="button is-primary is-outlined next-button"
                          :aria-label="$t('threeD.nextResult')"
                          @click="nextResult">
            <span class="icon is-medium">
              <i class="fa fa-chevron-right" />
            </span>
                  </button>
                </p>
              </search-type-ahead>
              <toc-content-tabs v-if="enableThreeDContent"/>
              <div v-if="activeContent === '2d'"
                   class="buttons has-addons toc-expand-collapse">
                <button class="button expand-button" @click="expand">
              <span class="icon">
                <i class="fa fa-lg fa-plus-square"/>
              </span>
                  <span>{{ $t('expandAll') }}</span>
                </button>
                <button class="button collapse-button" @click="collapse">
              <span class="icon">
                <i class="fa fa-lg fa-minus-square"/>
              </span>
                  <span>{{ $t('collapseAll') }}</span>
                </button>
              </div>
            </div>
            <aside v-if="activeContent === '2d'"
                   class="menu is-paddingless">
              <ul v-for="item in searchFilteredTocItems"
                  :key="item.id"
                  class="menu-list"
                  :class="{'menu-list-highlighted': isHighlighted(item)}">
                <table-of-contents-item :item="item"
                                        :item-path="getItemPath(item)"
                                        :highlighted="searchResults"
                                        :force-open="forceOpen"
                                        :is-widget="isWidget"
                                        :flat-list-idx="getFlatListIdx(getItemPath(item))"
                                        :widget-content-path="widgetContentPath"
                                        @select="select"
                                        @widgetNav="widgetNav"
                />
              </ul>
            </aside>
            <toc-three-d-content v-else/>
            <div class="dropdown-footer"/>
          </div>
        </transition>
      </div>

    </div>
    <div v-if="expanded" class="toc-resizer" @mousedown.stop.prevent="startResize" @click.stop />
  </div>
</template>

<script setup lang="ts">
import { useStore} from 'vuex'
import { ref, computed, onMounted, onBeforeUnmount, watch, nextTick } from "vue"
import { useRoute } from 'vue-router'
import TableOfContentsItem from "@/components/library/content/header/TableOfContentsItem.vue";
import SearchTypeAhead from "@/components/common/search/SearchTypeAhead.vue";
import {useWidgetToc} from "@/components/widgets/composables/useWidgetToc";
import TryThisPill from "@/components/common/TryThisPill.vue";
import TocContentTabs from '@/components/library/content/TocContentTabs.vue'
import TocThreeDContent from '@/components/library/content/TocThreeDContent.vue'
import { getThreeDTocSearchResultKey } from '@/helpers/threeDTocSearch'
const { contentPath,
  useWidgetTocNav, getFlatListIdx } = useWidgetToc()
const store = useStore()
const route = useRoute()

const props = withDefaults(defineProps<{
  enableThreeDContent?: boolean
  query: string
}>(), {
  enableThreeDContent: false
})

const persistedTocQuery = computed(() => {
  const q = store.state.search?.filters?.tocQuery
  return (typeof q === 'string') ? q : ''
})

const localQuery = ref(props.query || persistedTocQuery.value || '')

const expanded = ref(false)
const isResizing = ref(false)
const collapsedWidth = 50
const minPanelWidth = 220
const maxPanelWidth = 1200
const panelWidth = ref(350)

const tocStyle = computed(() => {
  const width = collapsedWidth + (expanded.value ? panelWidth.value : 0)
  return {
    flexBasis: `${width}px`,
    maxWidth: `${width}px`
  }
})

let resizeStartX = 0
let resizeStartWidth = 0
let suppressClick = false
const bookSearch = ref(false)
const highlightedInstance = ref(-1)
const forceOpen = ref(0)
const searchResults = ref<any[]>([])
const hasAutoNavigatedInitialResult = ref(false)
const autoSelectedQuery = ref('')
const searchPending = ref(false)
let searchRequestId = 0

const book = computed(() => store.state.content?.toc?.book)
const isWidget = computed(() => store.getters['widgets/isWidget'])
const selectBomItemsFromCallout = (item?: string) => store.dispatch('content/selectBomItemsFromCallout', item)
const searchContentNameLabel = computed(() => {
  const name = book.value?.name || ''
  return name.length > 25 ? name.substring(0, 25) : name
})
const toc = computed(() => store.state.content?.toc?.items)
const threeDModels = computed(() => store.getters['content/threeDModels'] || [])
const threeDSearchResults = computed<any[]>(() => store.getters['content/threeDTocSearchResults'] || [])
const selectedThreeDSearchResultKey = computed(() =>
  store.state.content?.toc?.selectedThreeDSearchResultKey || ''
)
const threeDModelSnapshotsLoaded = computed(() =>
  Boolean(store.state.content?.toc?.isThreeDModelSnapshotsLoaded)
)
const activeContent = computed(() => {
  return props.enableThreeDContent
    ? store.state.content?.toc?.activeContent
    : '2d'
})
const viewerActiveContent = computed(() =>
  store.state.content?.toc?.viewerActiveContent || activeContent.value
)
const isTocOpen = computed(() => store.state.content?.toc?.isTocOpen)
const widgetContentPath = computed(() => contentPath.value )

const countTocItems = (items: any[]): number => {
  return (items || []).reduce((acc, item) => {
    const children = Array.isArray(item?.children) ? item.children : []
    const type = (item?.type == null ? '' : String(item.type)).trim().toLowerCase()
    const isPage = type === 'page'
    return acc + (isPage ? 1 : 0) + countTocItems(children)
  }, 0)
}

const shouldShowFilteredCount = computed(() => Boolean(localQuery.value?.length))
const tocTotalCount = computed(() => countTocItems(toc.value || []) + threeDModels.value.reduce(
  (count: number, model: any) => count + 1 + (model.snapshots?.length || 0),
  0
))
const tocResultCount = computed(() => Number(store.state.content?.toc?.resultCount || 0))
const hasSearchResults = computed(() => tocResultCount.value > 0)
const tocTotalDisplayed = computed(() => {
  if (!tocQuery.value?.length) return tocTotalCount.value
  return tocResultCount.value
})

const storeInitialSearchQuery = computed(() => {
  const filters = store.state.search?.filters
  const tocQuery = filters?.tocQuery

  if (typeof tocQuery === 'string' && tocQuery.length) return tocQuery
  return ''
})

const path = computed(() => {
  return book.value ? `/${book.value.contentType}/${book.value.id}/` : ''
})

const getTOCSearchResults = (payload: any) => store.dispatch('content/tocSearchWithReturn', payload)
const updateTocOpen = (value: boolean) => store.dispatch('content/updateTocOpen', value)
const navigateToContentPath = (payload: any) => store.dispatch('content/navigateToContentPath', payload)

const tocQuery = computed(() => localQuery.value || '')
const hasTocQuery = computed(() => tocQuery.value?.length > 0)
let lastSearchQuery = tocQuery.value.trim()

const selectedFlatListIdx = ref(-1)

const highlightedTocItems = ref<Array<{ itemPath: string, flatListIdx: number }>>([])


const setActiveTocHighlighted = (elements: HTMLCollectionOf<Element>, idx: number) => {
  for (let i = 0; i < elements.length; i++) {
    elements.item(i)?.classList.remove('menu-list-highlighted-active')
  }
  elements.item(idx)?.classList.add('menu-list-highlighted-active')
}

const clearActiveTocHighlighted = () => {
  const active = document.getElementsByClassName('menu-list-highlighted-active')
  while ((active?.length ?? 0) > 0) {
    active.item(0)?.classList.remove('menu-list-highlighted-active')
  }
}

const scrollSelectedIntoView = async (itemPath: string) => {
  if (!expanded.value) return
  await nextTick()

  const panel = document.querySelector('.toc-panel') as HTMLElement | null
  if (!panel) return

  const selector = isWidget.value
    ? `#toc-item-${selectedFlatListIdx.value}`
    : `a[href*="${itemPath}"]`
  const el = panel.querySelector(selector) as HTMLElement | null
  if (!el) return

  el.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
}

const nextResult = () => {
  if (!canNavigateResults.value) return

  if (activeContent.value === '3d') {
    selectAdjacentThreeDResult(1)
    return
  }

  const results = highlightedTocItems.value
  if (results.length < 2) return

  const currentIndex = viewerActiveContent.value === '2d'
    ? results.findIndex(r => r.flatListIdx === selectedFlatListIdx.value)
    : -1
  const nextIndex = currentIndex >= 0
    ? (currentIndex + 1) % results.length
    : 0

  const nextItem = results[nextIndex]
  if (nextItem) select(nextItem.itemPath)

  const active = document.activeElement as HTMLElement | null
  active?.blur?.()
}

const previousResult = () => {
  if (!canNavigateResults.value) return

  if (activeContent.value === '3d') {
    selectAdjacentThreeDResult(-1)
    return
  }

  const results = highlightedTocItems.value
  if (results.length < 2) return

  const currentIndex = viewerActiveContent.value === '2d'
    ? results.findIndex(r => r.flatListIdx === selectedFlatListIdx.value)
    : -1
  const prevIndex = currentIndex >= 0
    ? (currentIndex - 1 + results.length) % results.length
    : (results.length - 1)

  const prevItem = results[prevIndex]
  if (prevItem) select(prevItem.itemPath)

  const active = document.activeElement as HTMLElement | null
  active?.blur?.()
}

const setActiveFromItemPath = (itemPath: string) => {
  const highlighted = document.getElementsByClassName('menu-list-highlighted')
  const count = highlighted?.length ?? 0
  if (count <= 0) return

  for (let i = 0; i < count; i++) {
    const el = highlighted.item(i) as HTMLElement | null
    const link = el?.querySelector('a')
    const href = link?.getAttribute('href') || ''
    if (href && href.includes(itemPath)) {
      highlightedInstance.value = i
      setActiveTocHighlighted(highlighted, i)
      return
    }
  }
}

const search = (query: string, bSearch: boolean) => {
  const requestId = ++searchRequestId
  const q = query || ''
  const normalizedQuery = q.trim()
  const queryChanged = normalizedQuery !== lastSearchQuery
  if (autoSelectedQuery.value !== normalizedQuery) autoSelectedQuery.value = ''
  searchPending.value = true
  localQuery.value = q
  if (queryChanged) {
    searchResults.value = []
    highlightedTocItems.value = []
    selectedFlatListIdx.value = -1
    highlightedInstance.value = -1
    clearActiveTocHighlighted()
  }
  if (q) {
    void store.dispatch('search/setTocQuery', q)
  } else {
    void store.dispatch('search/clearTocQuery')
  }
  return Promise.resolve(getTOCSearchResults({ query: q }))
    .then((results: any[]) => {
      if (requestId !== searchRequestId) return
      searchPending.value = false
      searchResults.value = results || []
      highlightedInstance.value = -1
      clearActiveTocHighlighted()

      lastSearchQuery = normalizedQuery
      rebuildHighlightedTocItems(!queryChanged)

      void autoSelectUnifiedResult()

      if (tocResultCount.value > 0) {
        expanded.value = true
        updateTocOpen(true)
        bookSearch.value = bSearch || false
      }
    })
}

const runInitialSearchFromStore = () => {
  if (hasAutoNavigatedInitialResult.value) return
  const q = storeInitialSearchQuery.value
  if (!q) return

  hasAutoNavigatedInitialResult.value = true

  if (!localQuery.value) localQuery.value = q
  void search(q, true)
}

const expand = () => {
  forceOpen.value += 1;
}

const collapse = () => {
  forceOpen.value -= 1;
}

const isHighlighted = (item: any) => {
  const normalizeId = (v: unknown) => (v == null ? '' : String(v))
  const normalizeType = (v: unknown) => (v == null ? '' : String(v).trim().toLowerCase())

  const itemId = normalizeId(item?.id)
  const itemType = normalizeType(item?.type)

  return (searchResults.value || []).some((tocItem: any) => {
    return normalizeId(tocItem?.entityId) === itemId &&
      normalizeType(tocItem?.entityType) === itemType
  })
}

const getItemPath = (item: any) => {
  return path.value + item.type + ':' + item.id
}

const select = async (itemPath: string) => {
  await store.dispatch('content/updateViewerActiveContent', '2d')
  if (tocQuery.value?.length) {
    setActiveFromItemPath(itemPath)
  }
  if (tocQuery.value?.length) {
    const idx = getFlatListIdx(itemPath)
    selectedFlatListIdx.value = idx
  }
  const q = localQuery.value || ''
  if (q && persistedTocQuery.value !== q) {
    await store.dispatch('search/setTocQuery', q)
  }

  if (isWidget.value) {
    await widgetNav(itemPath)
    return
  }

  await navigateToContentPath({
    path: itemPath
  })
}

const widgetNav = async (itemPath: string) => {
  const q = localQuery.value || persistedTocQuery.value || ''
  if (q) {
    await store.dispatch('search/setBomQuery', q)
  }
  await useWidgetTocNav(itemPath)
}

const toggleExpanded = () => {
  expanded.value = !expanded.value
  updateTocOpen(expanded.value)
}

const searchFilteredTocItems = computed(() => {
  const tocItems = toc.value || []
  if (!tocQuery.value?.length) return tocItems
  // If there is an active TOC search query but no results, show no items.
  if (!(searchResults.value || []).length) return []
  const normalizeId = (v: unknown) => (v == null ? '' : String(v))
  const normalizeType = (v: unknown) => (v == null ? '' : String(v).trim().toLowerCase())

  const resultKeys = new Set(
    (searchResults.value || [])
      .filter(r => r?.entityId != null && r?.entityType)
      .map(r => `${normalizeType(r.entityType)}:${normalizeId(r.entityId)}`)
  )

  const filterTree = (items: any[]) => {
    return (items || []).reduce((acc, item) => {
      const key = `${normalizeType(item?.type)}:${normalizeId(item?.id)}`
      const filteredChildren = item?.children ? filterTree(item.children) : undefined
      const keep = resultKeys.has(key) || (filteredChildren && filteredChildren.length > 0)

      if (keep) {
        acc.push({
          ...item,
          ...(filteredChildren ? { children: filteredChildren } : {})
        })
      }
      return acc
    }, [])
  }

  return filterTree(tocItems)
})

const rebuildHighlightedTocItems = (selectCurrent = true) => {
  const results: Array<{ itemPath: string, flatListIdx: number }> = []
  const walk = (items: any[], parentPath: string) => {
    (items || []).forEach((item) => {
      const itemPath = parentPath + item.type + ':' + item.id
      if (isHighlighted(item)) {
        const idx = getFlatListIdx(itemPath)
        results.push({ itemPath, flatListIdx: idx })
      }
      const children = Array.isArray(item?.children) ? item.children : []
      if (children.length) {
        walk(children, itemPath + '/')
      }
    })
  }
  walk(searchFilteredTocItems.value || [], path.value)
  results.sort((a, b) => a.flatListIdx - b.flatListIdx)
  highlightedTocItems.value = results

  if (!selectCurrent) {
    selectedFlatListIdx.value = -1
    return
  }

  const currentPath = isWidget.value ? widgetContentPath.value : route.path
  const current = results.find(r => r.itemPath === currentPath)
  selectedFlatListIdx.value = current ? current.flatListIdx : -1
}

const selectThreeDSearchResult = (searchResult: any) => store.dispatch(
  'content/selectThreeDTocSearchResult',
  { searchResult }
)

const autoSelectUnifiedResult = async () => {
  const query = tocQuery.value.trim()
  if (!query || searchPending.value || !threeDModelSnapshotsLoaded.value) return
  if (autoSelectedQuery.value === query) return

  const unifiedResultCount = highlightedTocItems.value.length + threeDSearchResults.value.length
  if (unifiedResultCount !== 1) return

  autoSelectedQuery.value = query
  if (highlightedTocItems.value.length === 1) {
    const only = highlightedTocItems.value[0]
    if (only?.itemPath && !isWidget.value) {
      await select(only.itemPath)
      await store.dispatch('search/setBomQuery', tocQuery.value)
    }
    return
  }

  await selectThreeDSearchResult(threeDSearchResults.value[0])
}

const selectAdjacentThreeDResult = (offset: number) => {
  const results = threeDSearchResults.value
  if (results.length < 2) return

  const currentIndex = results.findIndex(result =>
    getThreeDTocSearchResultKey(result) === selectedThreeDSearchResultKey.value
  )
  const nextIndex = currentIndex >= 0
    ? (currentIndex + offset + results.length) % results.length
    : (offset > 0 ? 0 : results.length - 1)
  void selectThreeDSearchResult(results[nextIndex])

  const active = document.activeElement as HTMLElement | null
  active?.blur?.()
}

const canNavigateResults = computed(() => !searchPending.value && (
  activeContent.value === '3d'
    ? threeDSearchResults.value.length > 1
    : highlightedTocItems.value.length > 1
))

const clearClick = async () => {
  searchRequestId++
  autoSelectedQuery.value = ''
  lastSearchQuery = ''
  searchPending.value = false
  localQuery.value = ''
  searchResults.value = []
  highlightedTocItems.value = []
  selectedFlatListIdx.value = -1
  highlightedInstance.value = -1
  clearActiveTocHighlighted()

  void store.dispatch('search/clearTocQuery')
  await getTOCSearchResults({query: ''})
}

const onResizeMove = (e: MouseEvent) => {
  if (!isResizing.value) return
  const next = resizeStartWidth + (e.clientX - resizeStartX)
  panelWidth.value = Math.max(minPanelWidth, Math.min(maxPanelWidth, next))
}

const onResizeClickCapture = (e: MouseEvent) => {
  if (!suppressClick) return
  e.preventDefault()
  e.stopPropagation()
}
const onResizeEnd = () => {
  if (!isResizing.value) return
  isResizing.value = false
  window.removeEventListener('mousemove', onResizeMove)
  window.removeEventListener('mouseup', onResizeEnd)

  // Prevent the click that commonly fires after a drag resize from closing the panel.
  suppressClick = true
  window.addEventListener('click', onResizeClickCapture, true)
  window.setTimeout(() => {
    suppressClick = false
    window.removeEventListener('click', onResizeClickCapture, true)
  }, 0)
}
const startResize = (e: MouseEvent) => {
  isResizing.value = true
  resizeStartX = e.clientX
  resizeStartWidth = panelWidth.value
  window.addEventListener('mousemove', onResizeMove)
  window.addEventListener('mouseup', onResizeEnd)
}

watch(() => props.query, (next, oldValue) => {
  const nextValue = next || ''
  // Avoid re-running the search when the prop update is just reflecting
  // a query we already applied locally (prevents duplicate tocSearch calls).
  if (nextValue === oldValue) return
  if (nextValue === localQuery.value) {
    if (nextValue && !(searchResults.value || []).length) {
      search(nextValue, true)
    }
    return
  }
  if (!nextValue && persistedTocQuery.value) {
    localQuery.value = persistedTocQuery.value
    if (!(searchResults.value || []).length) {
      search(localQuery.value, true)
    }
    return
  }
  localQuery.value = nextValue
  if (localQuery.value) {
    search(localQuery.value, true)
  }
})

watch(() => book.value?.id, () => {
  autoSelectedQuery.value = ''
  lastSearchQuery = ''
})

watch(
  () => [
    tocQuery.value,
    searchPending.value,
    threeDModelSnapshotsLoaded.value,
    highlightedTocItems.value.map(result => result.itemPath).join(','),
    threeDSearchResults.value.map(getThreeDTocSearchResultKey).join(',')
  ],
  () => {
    void autoSelectUnifiedResult()
  }
)

watch(persistedTocQuery, (next) => {
  if (!next) return
  if (next === localQuery.value) {
    if (!(searchResults.value || []).length) {
      search(localQuery.value, true)
    }
    return
  }
  localQuery.value = next
  if (!(searchResults.value || []).length) {
    search(localQuery.value, true)
  }
})

watch(isTocOpen, (open, wasOpen) => {
  expanded.value = Boolean(open)
  if (!open || wasOpen) return
  runInitialSearchFromStore()
})

watch(
  () => [route.path, widgetContentPath.value],
  () => {
    if (!hasTocQuery.value) return
    const currentPath = isWidget.value ? widgetContentPath.value : route.path
    const idx = getFlatListIdx(currentPath)
    selectedFlatListIdx.value = idx
  }
)
//watcher ensures that regardless of source (nav buttons, SableOfContentsStepper)
// or desktop or widgets
// a change in the active page gets results in a scroll into view
watch(
  () => [selectedFlatListIdx.value, route.path, widgetContentPath.value, expanded.value],
  async () => {
    const currentPath = isWidget.value ? widgetContentPath.value : route.path
    await scrollSelectedIntoView(currentPath)
  },
  { flush: 'post' }
)

onMounted(() => {
  expanded.value = isTocOpen.value

  if (isWidget.value) {
    const q = persistedTocQuery.value
    if (q && localQuery.value !== q) localQuery.value = q
  }

  if (storeInitialSearchQuery.value) {
    runInitialSearchFromStore()
    return
  }

  // Fallback to the local query (prop/persisted) when store doesn't have one.
  const q = localQuery.value
  if (q) search(q, true)
})

onBeforeUnmount(() => {
  onResizeEnd()
})

</script>
<style scoped lang="scss">
$collapsed-width: 50px;

.toc-navigation {
  border-right: 1px solid #EDEDED;
  background-color: #fff;
  overflow-x: hidden;
  overflow-y: hidden;
  transition: flex-basis 420ms cubic-bezier(0.22, 1, 0.36, 1), max-width 420ms cubic-bezier(0.22, 1, 0.36, 1);
  will-change: flex-basis, max-width;
  display: flex;
  flex-flow: column nowrap;
  justify-content: flex-start;
  flex: 0 0 $collapsed-width;
  max-width: 30%;
  position: relative;
  z-index: 20;
  height: 100%;
  max-height: 100%;
  min-height: 0;

  &.expanded {
    flex: 0 0 30%;
    overflow: hidden;
  }

  /* Intentionally avoid styling all icons globally; handle icons are styled below */
}

.toc-navigation.is-widget {
  overflow-y: hidden;
  align-self: stretch;
  height: 90%;
  max-height: 90%;
}

/* Allow the main content area to fill available height so the TOC can scroll */
.toc-navigation > div {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.toc-header {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  background: #fff;
  position: relative;
  flex: 0 0 auto;
}

.toc-try-this-attached {
  position: absolute;
  top: -0.25em;
  bottom: auto;
  right: 0;
  z-index: 300;
}

.toc-try-this-attached :deep(.tooltip .tooltiptext.is-bottom),
.toc-try-this-attached :deep(.tooltip .tooltiptext.is-top) {
  transform: translateX(-65%);
}

.toc-search {
  width: 100%;
  display: flex;
  position: relative;
  z-index: 200;
  padding-top: 1.5rem;
  margin-bottom: 0.5rem;
}

.toc-search > :deep(.control) {
  flex: 0 0 auto;
}

.toc-search > :deep(.control.is-expanded) {
  flex: 1 1 auto;
  min-width: 0;
}

.toc-search > :deep(.control.is-expanded) :deep(.input),
.toc-search > :deep(.control.is-expanded) :deep(input) {
  width: 100%;
  min-width: 0;
}

.toc-expand-collapse {
  width: 100%;
  justify-content: space-between;
  position: relative;
  z-index: 1;
}

.toc-expand-collapse .button {
  min-width: 120px;
}

.menu-list-highlighted-active {
  outline: 2px solid var(--primary);
}

.toc-navigation :deep(.menu-list-highlighted) {
  border-left-style: solid;
  border-left-width: .25rem;
}

.item {
  cursor: pointer;
  min-height: $collapsed-width;
  height: auto;
  width: auto;
  white-space: nowrap;
  .icon {
    display: flex;
    height: $collapsed-width;
    width: $collapsed-width;
  }
  &:hover {
    background-color: #EDEDED;
  }
}

.icon-label {
  flex: 0 0 0;
}

.collapse {
  display: flex;
  flex: 1 1 auto;
  min-height: 0;
}

.toc-button-with-results {
  padding-right: .25rem;
}

.toc-panel {
  flex: 1 1 auto;
  min-width: 0;
  min-height: 0;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  overflow-x: visible;
  box-sizing: border-box;
}

.toc-navigation.is-widget .toc-panel {
  max-height: 98%;
}

.toc-panel .menu {
  flex: 1 1 auto;
  min-height: 0;
  overflow: auto;
  margin-right: 10px;
  box-sizing: border-box;
  overscroll-behavior: contain;
  padding-bottom: 1rem;
  scrollbar-gutter: stable;
  scrollbar-width: thin;
  scrollbar-color: #b5b5b5 #f5f5f5;
}

.toc-panel .menu::-webkit-scrollbar {
  width: 10px;
}

.toc-panel .menu::-webkit-scrollbar-track {
  background: #f5f5f5;
}

.toc-panel .menu::-webkit-scrollbar-thumb {
  background: #b5b5b5;
  border-radius: 8px;
  border: 2px solid #f5f5f5;
}

.toc-panel .menu::-webkit-scrollbar-thumb:hover {
  background: #999;
}

.toc-resizer {
  position: absolute;
  top: 0;
  right: 0;
  width: 10px;
  height: 100%;
  cursor: col-resize;
  z-index: 10;
  background-color: #fff;
  border-left: 1px solid #EDEDED;
}

.toc-resizer::before {
  content: '';
  display: block;
  width: 3px;
  height: 40px;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  border-left: 1px solid #ccc;
  border-right: 1px solid #ccc;
}

.toc-resizer:hover::before {
  border-left-color: #999;
  border-right-color: #999;
}

.toc-content-enter-active,
.toc-content-leave-active {
  transition: none;
}

.toc-content-enter-from,
.toc-content-leave-to {
  opacity: 1;
  transform: none;
}


.toc-navigation.is-resizing {
  user-select: none;
}

.toc-navigation.is-resizing,
.toc-navigation.is-resizing * {
  cursor: col-resize;
}
</style>
